/**
 * RecentCommunications Component
 * 
 * Displays a list of recent communications and updates for the customer.
 * Shows communication type, title, and timestamp.
 * 
 * Requirements: 7.3 - Display a list of recent communications and updates
 */

import { Card, CardContent, CardHeader, CardTitle } from '@bloomcode/ui';
import { Ticket, FileText, Bell, Mail, MessageSquare } from '@bloomcode/icons';

export type CommunicationType = 'ticket' | 'invoice' | 'announcement' | 'email' | 'message';

export interface Communication {
  id: string;
  type: CommunicationType;
  title: string;
  description?: string;
  date: string;
  isRead?: boolean;
  link?: string;
}

interface RecentCommunicationsProps {
  communications: Communication[];
  isLoading?: boolean;
  onCommunicationClick?: (communication: Communication) => void;
}

function RecentCommunicationsSkeleton() {
  return (
    <Card>
      <CardHeader>
        <div className="h-5 w-44 animate-pulse rounded bg-muted" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="h-8 w-8 animate-pulse rounded-full bg-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

const communicationIcons: Record<CommunicationType, React.ReactNode> = {
  ticket: <Ticket className="h-4 w-4" />,
  invoice: <FileText className="h-4 w-4" />,
  announcement: <Bell className="h-4 w-4" />,
  email: <Mail className="h-4 w-4" />,
  message: <MessageSquare className="h-4 w-4" />,
};

const communicationColors: Record<CommunicationType, string> = {
  ticket: 'bg-primary/10 text-primary',
  invoice: 'bg-success/10 text-success',
  announcement: 'bg-accent/10 text-accent',
  email: 'bg-warning/10 text-warning',
  message: 'bg-warning/10 text-warning',
};

const communicationLabels: Record<CommunicationType, string> = {
  ticket: 'Support Ticket',
  invoice: 'Invoice',
  announcement: 'Announcement',
  email: 'Email',
  message: 'Message',
};

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface CommunicationItemProps {
  communication: Communication;
  onClick?: () => void;
}

function CommunicationItem({ communication, onClick }: CommunicationItemProps) {
  const icon = communicationIcons[communication.type];
  const colorClass = communicationColors[communication.type];
  const label = communicationLabels[communication.type];

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start gap-3 rounded-lg p-2 text-left transition-colors hover:bg-muted/50"
    >
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${colorClass}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`text-sm font-medium truncate ${!communication.isRead ? 'font-semibold' : ''}`}>
            {communication.title}
          </p>
          {!communication.isRead && (
            <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{label}</span>
          <span>•</span>
          <span>{formatRelativeTime(communication.date)}</span>
        </div>
        {communication.description && (
          <p className="mt-1 text-xs text-muted-foreground line-clamp-1">
            {communication.description}
          </p>
        )}
      </div>
    </button>
  );
}

export function RecentCommunications({ 
  communications, 
  isLoading, 
  onCommunicationClick 
}: RecentCommunicationsProps) {
  if (isLoading) {
    return <RecentCommunicationsSkeleton />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Recent Communications</CardTitle>
      </CardHeader>
      <CardContent>
        {communications.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            No recent communications
          </div>
        ) : (
          <div className="space-y-1">
            {communications.map((communication) => (
              <CommunicationItem
                key={communication.id}
                communication={communication}
                onClick={() => onCommunicationClick?.(communication)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default RecentCommunications;
